import { MatDialogRef } from '@angular/material/dialog';

import { DropDownFilterSettings, VirtualizationSettings } from '@progress/kendo-angular-dropdowns';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';

import { BulkSiteUploadShapefileSaveSitesDialogComponent } from '../../../../components/dialogs/bulk-site-upload-dialog/shapefile/bulk-site-upload-shapefile-save-sites-loader-dialog/bulk-site-upload-shapefile-save-sites-dialog.component';
import { App, GeoJsonFile, Project, Site, WizardStep } from '../../../../models';
import { ShapefileInfo } from '../../../../models/shapefile-info';
import { AliasesDefaultValues, BulkSiteUploadShapefileActivateSitesStep } from '../../../../shared/enums';

/* istanbul ignore file */
export class BulkSiteUploadShapefileModel {
    virtualizationSettings: VirtualizationSettings = { itemHeight: 32, pageSize: 25 };
    filterSettings: DropDownFilterSettings = {
        caseSensitive: false,
        operator: "contains",
    };

    sites: Site[] = [];
    geoJsonFile: GeoJsonFile | undefined;
    chargeNumberValidities: { [key: string]: boolean; } = {};
    fileUploadStep: ShapefileInfo = new ShapefileInfo();
    matchingAttributesStep: MatchingAttributesModel = new MatchingAttributesModel();
    activateSitesStep: ActivateSitesModel = new ActivateSitesModel();
    reviewStep: ReviewStepModel = new ReviewStepModel();
    overrideSitesStep: OverrideSitesModel = new OverrideSitesModel();
    saveSitesStep: SaveSitesModel = new SaveSitesModel();
    currentApp: App;
    currentProject: Project = new Project();
    stepper: BulkSiteUploadStepperModel;

    constructor(app: App) {
        this.currentApp = app;
        this.stepper = new BulkSiteUploadStepperModel(this.currentApp);
    }
}

class BulkSiteUploadStepperModel {
    perSiteShapefileWizardSteps: WizardStep[] = [
        new WizardStep(`Upload File`, 'upload_2'),
        new WizardStep(`Match Attributes`, 'sync_alt'),
        new WizardStep(`Add Details`, 'list_alt'),
        new WizardStep('Review & Submit', 'check')
    ];

    enterpriseShapefileWizardSteps: WizardStep[] = [
        new WizardStep(`Upload File`, 'upload_2'),
        new WizardStep(`Match Attributes`, 'sync_alt'),
        new WizardStep(`Activate ${this.sitesAlias}`, 'bolt'),
        new WizardStep('Review & Submit', 'check')
    ];

    excelWizardSteps: WizardStep[] = [
        new WizardStep(`Upload File`, 'upload_2'),
        new WizardStep('Review & Submit', 'check')
    ];

    siteTemplateWizardStep: WizardStep = new WizardStep(`${this.siteAlias} Template`, 'space_dashboard');

    get siteAlias(): string {
        return this._currentApp?.aliases.site.singular ?? AliasesDefaultValues.SingularSite;
    }

    get sitesAlias(): string {
        return this._currentApp?.aliases.site.plural ?? AliasesDefaultValues.PluralSite;
    }

    constructor(private _currentApp: App) { }
}

export class MatchingAttributesModel {
    siteNoProperty = "";
    siteNameProperty = "";
    siteChargeNoProperty = "";
    properties: string[] = [];
    commonProperties: string[] = [];
    commonPropertiesUniqueValues: string[] = [];
    firstSiteNoValue: string | undefined;
    firstSiteNameValue: string | undefined;
    firstSiteChargeNoValue: string | undefined;
    keysUniqueValue = new Map<string, Set<string>>();
    showErrorMessage = false;
}

export class ActivateSitesModel {
    isFetchingChargeNumberValidities = false;
    activateSitesSubStep = BulkSiteUploadShapefileActivateSitesStep.ActivateSites;
    showErrorMessage = false;
    //used to determine which site was just selected, kept empty for the dropdown
    modeledSites: Site[] = [];
    selectedSites: Site[] = [];
    filteredSitesThatCanBeActivated: Site[] = [];
    sitesThatCanBeActivated: Site[] = [];
    selectAllSite: Site = new Site();
    selectAllSiteNo = '-1';
    selectAllMessage = `Select all`;
    activateSites: boolean | undefined;
    showCustomPlaceholder = true;
    sitesMissingChargeNumberAfterMatchingAttributes: Site[] = [];
    associateChargeNumberSubStep = new AssociateChargeNumberStep();
    isAssociateChargeNumbersStepApartOfTheUploadFlow = false;
}

export class ReviewStepModel {
    isEditing = false;
    siteNoBeingEdited: string | undefined = undefined;
    editingChargeNumberOriginalValue = '';
    initialSitesToFix: Site[] = [];
    sitesLeftToFix: Site[] = [];
    dataSource: GridDataResult | undefined;
    state: DataStateChangeEvent = {
        skip: 0,
        take: 100,
        filter: {
            logic: 'and',
            filters: []
        }
    };
}

class OverrideSitesModel {
    overriddenSites: Site[] = [];
    dataSource: GridDataResult | undefined;
    state: DataStateChangeEvent = {
        skip: 0,
        take: 100,
        filter: {
            logic: 'and',
            filters: []
        }
    };
}

class SaveSitesModel {
    percentComplete = 0;
    sentSites: Site[] = [];
    sitesBeingSent: Site[] = [];
    sitesToSend: Site[] = [];
    totalSitesToSend: number | undefined;
    currentApp: App | undefined;
    currentProject: Project | undefined;
    saveSitesDialogRef: MatDialogRef<BulkSiteUploadShapefileSaveSitesDialogComponent> | undefined;
    numberOfSitesToSaveAtOnce = 50;
    msBetweenCheckingSaves = 3500;
    areSitesBeingSaved = false;
    selectedSiteTemplateId = "";
}

export class AssociateChargeNumberStep {
    sitesStillMissingChargeNumbers: Site[] = [];
    associateChargeNumbers: AssociateChargeNumber[] = [];
}

export class AssociateChargeNumber {
    chargeNumber = '';
    isEditingChargeNumber = false;
    chargeNumberValid = false;
    chargeNumberValidating = false;
    selectAllSite: Site = new Site();
    //used to determine which site was just selected, kept empty for the dropdown
    modeledSites: Site[] = [];
    selectedSites: Site[] = [];
    filteredSites: Site[] = [];
    potentialSites: Site[] = [];
    showCustomPlaceholder = true;
    sitesPlaceholder = '';
}
